// frontend/src/components/setup/SharpenBox.tsx
import { useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { Sharpened, SharpenMark } from "@/hooks/useSetup";
import { markedLine } from "@/lib/setup/grades";
import { httpError } from "./http";
import { LoadingLine } from "./LoadingLine";

export type Accepted = "used" | "edited";

/** ③'s fix-the-question box. With wrong marks it offers a suggested question;
 *  without, it is a plain editor. The parent owns the request (`onSharpen`),
 *  saves + regrades in `onApply` (true = saved; the parent then closes the box)
 *  and keys the box on the marks so a changed mark drops a stale suggestion. */
export function SharpenBox({ question, wrong, sharpening, busy, onSharpen, onApply, onKeep }: {
  question: string;
  wrong: SharpenMark[];
  sharpening: boolean;
  busy: boolean;
  onSharpen: () => Promise<Sharpened>;
  onApply: (question: string, accepted: Accepted | null) => Promise<boolean>;
  onKeep: (sawSuggestion: boolean) => void;
}) {
  const [suggestion, setSuggestion] = useState<Sharpened | null>(null);
  const [text, setText] = useState(question);
  const [editing, setEditing] = useState(wrong.length === 0);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const line = wrong.length ? markedLine(wrong) : null;

  async function ask() {
    setError(null);
    try {
      const s = await onSharpen();
      setSuggestion(s);
      setText(s.question);
    } catch (err) {
      const { status, detail } = await httpError(err);
      setError(status === 429 && detail ? detail : "Couldn't suggest a sharper question. You can edit it yourself.");
      setText(question);
      setEditing(true);
    }
  }

  function apply() {
    const trimmed = text.trim();
    const accepted: Accepted | null = suggestion ? (trimmed === suggestion.question ? "used" : "edited") : null;
    void onApply(trimmed, accepted); // on false the box stays as is — retry the same suggestion
  }

  function editMyself() {
    setEditing(true);
    boxRef.current?.focus();
  }

  const showEditor = suggestion !== null || editing;

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-muted px-4 py-3.5">
      {line && (
        <div className="flex gap-3 text-sm">
          <Sparkles aria-hidden className="mt-0.5 size-4 shrink-0 text-primary" />
          <div>
            <p>{line.lead}</p>
            {line.bullets.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-muted-foreground">{line.bullets.map((b) => <li key={b}>{b}</li>)}</ul>
            )}
          </div>
        </div>
      )}

      {sharpening && <LoadingLine>Sharpening your question…</LoadingLine>}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

      {showEditor && (
        <div className="flex flex-col gap-1">
          <label htmlFor="sharpen-question" className="text-sm font-medium">
            {suggestion ? "Suggested question" : "Your question"}
          </label>
          <Textarea id="sharpen-question" ref={boxRef} rows={3} className="bg-background" value={text}
            onChange={(e) => setText(e.target.value)} />
          {suggestion && suggestion.changes.length > 0 && (
            <p className="text-sm text-muted-foreground">Adds: {suggestion.changes.join(" · ")}</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!showEditor && (
          <>
            <Button variant="outline" className="border-primary bg-transparent text-primary hover:bg-primary/10 dark:border-primary dark:bg-transparent dark:hover:bg-primary/10"
              onClick={ask} disabled={sharpening || busy}>Sharpen &amp; regrade</Button>
            <Button variant="ghost" onClick={() => onKeep(false)} disabled={sharpening}>Keep mine</Button>
          </>
        )}
        {suggestion && (
          <>
            <Button onClick={apply} disabled={!text.trim() || busy}>Use this &amp; regrade</Button>
            {!editing && <Button variant="outline" onClick={editMyself}>Edit it myself</Button>}
            <Button variant="ghost" onClick={() => onKeep(true)}>Keep mine</Button>
          </>
        )}
        {!suggestion && editing && (
          <>
            <Button onClick={apply} disabled={!text.trim() || busy}>Regrade</Button>
            <Button variant="ghost" onClick={() => onKeep(false)}>Cancel</Button>
          </>
        )}
      </div>
    </div>
  );
}
